import { useCallback, useEffect, useState } from 'react';
import { GestureResponderEvent, Platform, View } from 'react-native';

interface UseDragEventsProps {
  currentTimeInSeconds: number;
  onDragEnd?: () => void;
  onDragStart?: () => void;
  onSeeking?: (time: number) => void;
  progressBarRef: React.RefObject<View | null>;
  totalDurationSeconds?: number;
  setIsWatchingLive?: (isWatching: boolean) => void;
  calculateIsWatchingLive?: (seekTime?: number) => void;
  isLive?: boolean;
  setHasManuallySeekd?: (hasSeekd: boolean) => void;
}

interface UseDragEventsResponse {
  dragging: boolean;
  dragPercentage: number;
  dragTime: number;
  handleMouseDown: (event: React.MouseEvent<HTMLDivElement>) => void;
  handleMoveEnd: () => void;
  handlePressIn: (event: GestureResponderEvent) => void;
  handleTouchMove: (event: React.TouchEvent<HTMLDivElement>) => void;
  tooltipPositionPercentage: number;
  tooltipTime: number;
}

/**
 * Hook to handle drag events for the player timeline
 */
export default ({
  currentTimeInSeconds,
  onDragEnd,
  onDragStart,
  onSeeking,
  progressBarRef,
  totalDurationSeconds,
  setIsWatchingLive,
  calculateIsWatchingLive,
  isLive,
  setHasManuallySeekd,
}: UseDragEventsProps): UseDragEventsResponse => {
  const [dragging, setDragging] = useState(false);
  const [dragPercentage, setDragPercentage] = useState(0);
  const [dragTime, setDragTime] = useState(currentTimeInSeconds);
  const [tooltipPositionPercentage, setTooltipPositionPercentage] = useState(0);
  const [tooltipTime, setTooltipTime] = useState(0);

  const calculatePercentage = useCallback(
    (currentPosition: number, totalWidth: number) => {
      if (!totalWidth) return 0;

      return (currentPosition / totalWidth) * 100;
    },
    []
  );

  const calculateCurrentTime = useCallback(
    (currentPercentage: number) => {
      if (!totalDurationSeconds) return 0;

      return (currentPercentage / 100) * totalDurationSeconds;
    },
    [totalDurationSeconds]
  );

  const calculateTimePosition = useCallback(
    (positionX: number, totalWidth: number) => {
      if (!totalWidth) return { percentage: 0, time: 0 };

      const pointerX = Math.max(0, Math.min(totalWidth, positionX));
      const percentage = calculatePercentage(pointerX, totalWidth);

      return {
        percentage,
        time: calculateCurrentTime(percentage),
      };
    },
    [calculateCurrentTime, calculatePercentage]
  );
  const handleDrag = useCallback(
    (positionX: number, totalWidth: number) => {
      if (!totalWidth) return;

      const { percentage, time } = calculateTimePosition(positionX, totalWidth);

      setDragPercentage(percentage);
      setDragTime(time);
    },
    [calculateTimePosition]
  );

  const handleHover = useCallback(
    (positionX: number, totalWidth: number) => {
      if (!totalWidth) return;

      const { percentage, time } = calculateTimePosition(positionX, totalWidth);

      setTooltipPositionPercentage(percentage);
      setTooltipTime(time);
    },
    [calculateTimePosition]
  );

  const handleMove = useCallback(
    (pageX: number) => {
      if (progressBarRef.current && totalDurationSeconds) {
        progressBarRef.current.measure((_, __, width, ___, px) => {
          const pointerX = pageX - px;
          handleDrag(pointerX, width);
        });
        setDragging(true);
        onDragStart?.();
      }
    },
    [progressBarRef, totalDurationSeconds, onDragStart, handleDrag]
  );

  const handleTouchMove = useCallback(
    (event: React.TouchEvent<HTMLDivElement>) => {
      setIsWatchingLive?.(false);

      if (isLive) {
        setHasManuallySeekd?.(true);
      }

      handleMove(event.changedTouches[0].pageX);
    },
    [handleMove, setIsWatchingLive, setHasManuallySeekd, isLive]
  );

  const handleMouseDown = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      setIsWatchingLive?.(false);

      if (isLive) {
        setHasManuallySeekd?.(true);
      }

      handleMove(event.pageX);
    },
    [handleMove, setIsWatchingLive, setHasManuallySeekd, isLive]
  );

  const handlePressIn = useCallback(
    (event: GestureResponderEvent) => {
      setIsWatchingLive?.(false);

      // Mark that the user has manually seeked when they interact with the timeline
      if (isLive) {
        setHasManuallySeekd?.(true);
      }

      handleMove(event.nativeEvent.pageX);
    },
    [handleMove, setIsWatchingLive, setHasManuallySeekd, isLive]
  );

  const handleMoveEnd = useCallback(() => {
    if (!dragging) return;

    setDragging(false);

    if (onSeeking && totalDurationSeconds) {
      const seekTime = calculateCurrentTime(dragPercentage);
      onSeeking(seekTime);

      if (isLive) {
        calculateIsWatchingLive?.(seekTime);
      }
    }

    onDragEnd?.();
  }, [
    dragPercentage,
    dragging,
    onDragEnd,
    onSeeking,
    totalDurationSeconds,
    isLive,
    calculateIsWatchingLive,
    calculateCurrentTime,
  ]);

  const handleGlobalMove = useCallback(
    (pageX: number) => {
      if (!progressBarRef.current) return;

      progressBarRef.current.measure((_, __, width, ___, px) => {
        if (!width) return;

        const pointerX = pageX - px;

        if (dragging) {
          handleDrag(pointerX, width);
        }

        handleHover(pointerX, width);
      });
    },
    [handleDrag, handleHover, dragging, progressBarRef]
  );

  // Hook up event listeners for web
  useEffect(() => {
    if (Platform.OS !== 'web') return;

    const handleGlobalMouseMove = (event: MouseEvent) => {
      handleGlobalMove(event.pageX);
    };

    const handleGlobalTouchMove = (event: TouchEvent) => {
      handleGlobalMove(event.touches[0].pageX);
    };

    const handleGlobalMoveEnd = () => {
      if (dragging) {
        handleMoveEnd();
      }
    };

    window.addEventListener('mousemove', handleGlobalMouseMove);
    window.addEventListener('mouseup', handleGlobalMoveEnd);
    window.addEventListener('touchend', handleGlobalMoveEnd);
    window.addEventListener('touchmove', handleGlobalTouchMove);
    window.addEventListener('touchstart', handleGlobalTouchMove);

    return () => {
      window.removeEventListener('mousemove', handleGlobalMouseMove);
      window.removeEventListener('mouseup', handleGlobalMoveEnd);
      window.removeEventListener('touchend', handleGlobalMoveEnd);
      window.removeEventListener('touchmove', handleGlobalTouchMove);
      window.removeEventListener('touchstart', handleGlobalTouchMove);
    };
  }, [handleGlobalMove, dragging, handleMoveEnd]);

  return {
    dragPercentage,
    dragTime,
    handleMouseDown,
    handleMoveEnd,
    handlePressIn,
    handleTouchMove,
    tooltipPositionPercentage,
    tooltipTime,
    dragging,
  };
};
